import { CalendarRange, ChevronLeft, ChevronRight, Lock, Printer } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import { Hours } from '@obraplan/core/domain/shared/hours';
import { WEEKLY_PLAN_STATUS_LABEL } from '@obraplan/core/domain/planning/weekly-plan';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/states';
import { DailyScheduleReport } from '@/features/planning/DailyScheduleReport';
import { DailyScheduleSheet } from '@/features/planning/DailyScheduleSheet';
import { MoveAllocationDialog } from '@/features/planning/MoveAllocationDialog';
import { ProjectVersionNotice } from '@/features/projects/ProjectVersionNotice';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';
import { useLive } from '@/lib/use-live';

/**
 * Programação do dia (Q11): o que cada funcionário **vai** fazer hoje — a folha vai para o
 * canteiro antes de o dia começar. Não há lançamento; alocar é do planejamento semanal, e o
 * dia só recebe "mover para outro dia". O executado é assunto da Conferência.
 */
export function DailyPlanPage() {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const canPlan = usePermission(PERMISSION.LABOR_PLAN);

  const [date, setDate] = useState(() => container.clock.today('America/Sao_Paulo'));
  const [moving, setMoving] = useState<
    { allocationId: string; version: number; resourceName: string } | undefined
  >(undefined);
  // `undefined` imprime a folha do dia inteiro (o estado normal); um id estreita para a
  // ficha daquela pessoa, só até sair.
  const [printingResourceId, setPrintingResourceId] = useState<string | undefined>(undefined);

  // Imprime só depois de o impresso estreitado estar pintado: `print()` lê o DOM do
  // instante, e chamá-lo no mesmo gesto do clique sairia com a folha inteira no lugar da ficha.
  useEffect(() => {
    if (printingResourceId === undefined) return undefined;
    const timer = globalThis.setTimeout(() => {
      globalThis.print();
      setPrintingResourceId(undefined);
    }, 0);
    return () => {
      globalThis.clearTimeout(timer);
    };
  }, [printingResourceId]);

  // O nome da obra vai no impresso: duas obras imprimindo o mesmo dia geram folhas
  // indistinguíveis sem ele.
  const project = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getProject.execute(activeProjectId, session),
    [session?.userId, activeProjectId],
  );

  const view = useLive(
    () =>
      session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getDailySchedule.execute(
            { projectId: activeProjectId, date: date.toString() },
            session,
          ),
    [session?.userId, activeProjectId, date.toString()],
  );

  const [moveAllocation, moveState] = useCommand(
    (input: {
      allocationId: string;
      date: string;
      expectedVersion: number;
      justification?: string;
    }) => container.moveAllocationToDay.execute(input, session!),
    {
      successMessage: (result) =>
        result.merged
          ? 'Movida e somada à alocação que já existia no dia de destino.'
          : 'Alocação movida.',
      silent: true,
      onSuccess: () => setMoving(undefined),
    },
  );

  if (activeProjectId === undefined) {
    return (
      <EmptyState
        title="Nenhuma obra selecionada"
        description="Escolha uma obra no seletor do topo para ver a programação do dia."
        icon={<CalendarRange className="size-8" />}
      />
    );
  }

  if (view === undefined) return <LoadingState label="Carregando o dia…" />;
  if (!view.ok) return <ErrorState message={view.error.message} code={view.error.code} />;

  const data = view.value;
  const readOnly = data.weekStatus === 'CLOSED' || !canPlan;
  const summary = data.schedule.summary;
  const isToday = data.today === data.schedule.date;

  return (
    <>
      {/* Na impressão sobra só o impresso: a tela inteira é escondida de uma vez. */}
      <div className="flex flex-col gap-6 print:hidden">
        <ProjectVersionNotice />
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Programação do dia</h1>
            <p className="text-(--color-content-muted)">
              O que cada funcionário <strong>vai fazer</strong> hoje — a folha do canteiro.
            </p>
          </div>

          <Button
            variant="secondary"
            className="min-h-11 w-full sm:min-h-9 sm:w-auto"
            disabled={data.schedule.rows.length === 0}
            onClick={() => {
              // Já é o escopo do dia: imprimir direto evita um quadro de espera à toa.
              setPrintingResourceId(undefined);
              globalThis.print();
            }}
          >
            <Printer aria-hidden="true" />
            Imprimir a folha
          </Button>
        </header>

        {/* ── Navegação por dia ───────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <div className="flex w-full items-center gap-1 md:w-auto">
            <Button
              variant="ghost"
              size="icon"
              className="size-11 shrink-0 md:size-9"
              aria-label="Dia anterior"
              onClick={() => setDate(date.subtractDays(1))}
            >
              <ChevronLeft aria-hidden="true" />
            </Button>
            <span className="min-w-0 flex-1 text-center text-sm font-medium md:min-w-64 md:text-base">
              {data.dateLabel}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="size-11 shrink-0 md:size-9"
              aria-label="Próximo dia"
              onClick={() => setDate(date.addDays(1))}
            >
              <ChevronRight aria-hidden="true" />
            </Button>
          </div>

          {!isToday && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDate(container.clock.today('America/Sao_Paulo'))}
            >
              Hoje
            </Button>
          )}

          <Badge tone={data.weekStatus === 'CLOSED' ? 'neutral' : 'info'}>
            Semana {data.isoWeek} · {WEEKLY_PLAN_STATUS_LABEL[data.weekStatus]}
          </Badge>

          {!data.schedule.isWorkday && <Badge tone="warn">Dia não útil</Badge>}
          {readOnly && canPlan && (
            <Badge tone="neutral" icon={<Lock aria-hidden="true" />}>
              Semana fechada — somente leitura
            </Badge>
          )}
        </div>

        {/* ── Resumo do dia ───────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <SummaryCard
            label="Em serviço"
            value={String(summary.workingResourceCount)}
            hint={`${String(summary.idleResourceCount)} sem programação`}
          />
          <SummaryCard
            label="Planejado"
            value={Hours.ofQuarters(summary.plannedQuarterHours).format()}
            hint={
              summary.servicePlannedQuarterHours > 0
                ? `+ ${Hours.ofQuarters(summary.servicePlannedQuarterHours).format()} em serviços`
                : `de ${Hours.ofQuarters(summary.capacityQuarterHours).format()} disponíveis`
            }
          />
          <SummaryCard
            label="Frentes abertas"
            value={String(summary.activityCount)}
            hint="Atividades com gente hoje"
          />
          <SummaryCard
            label="Acima da jornada"
            value={String(summary.overloadedCount)}
            hint="Funcionários com excesso no dia"
            tone={summary.overloadedCount > 0 ? 'danger' : 'ok'}
          />
        </div>

        <DailyScheduleSheet
          schedule={data.schedule}
          readOnly={readOnly}
          onPrintSlip={setPrintingResourceId}
          {...(readOnly ? {} : { onMove: setMoving })}
        />

        <MoveAllocationDialog
          open={moving !== undefined}
          onOpenChange={(open) => {
            if (!open) setMoving(undefined);
          }}
          resourceName={moving?.resourceName ?? ''}
          currentDate={data.schedule.date}
          isPending={moveState.isPending}
          {...(moveState.error === undefined ? {} : { errorMessage: moveState.error.message })}
          onConfirm={(input) => {
            if (moving === undefined) return;
            void moveAllocation({
              allocationId: moving.allocationId,
              date: input.date,
              expectedVersion: moving.version,
              ...(input.justification === undefined ? {} : { justification: input.justification }),
            });
          }}
        />
      </div>

      <DailyScheduleReport
        schedule={data.schedule}
        dateLabel={data.dateLabel}
        projectName={project !== undefined && project.ok ? project.value.name : undefined}
        {...(printingResourceId === undefined ? {} : { resourceId: printingResourceId })}
      />
    </>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  hint: string;
  tone?: 'ok' | 'danger';
}

function SummaryCard({ label, value, hint, tone }: SummaryCardProps) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-0.5 p-4">
        <p className="text-xs uppercase tracking-wide text-(--color-content-muted)">{label}</p>
        <p
          className={
            tone === 'danger'
              ? 'tabular text-xl font-semibold text-(--color-state-danger)'
              : 'tabular text-xl font-semibold'
          }
        >
          {value}
        </p>
        <p className="text-xs text-(--color-content-muted)">{hint}</p>
      </CardContent>
    </Card>
  );
}
